import { useState, useEffect } from 'react';
import { Music, Zap, Gift, Megaphone, Clock, X } from 'lucide-react';
import { Prize } from '../../services/prizes';

interface PrizeClaimBannerProps {
  prize: Prize;
  onClaim: () => void;
  onDismiss: () => void;
}

const TYPE_CONFIG: Record<string, { icon: typeof Music; label: string; cta: string; color: string }> = {
  pick_next_song: { icon: Music, label: 'Pick the next song!', cta: 'Choose a Song', color: 'emerald' },
  auto_queue_bypass: { icon: Zap, label: 'Your next request skips the line!', cta: 'Claim Prize', color: 'amber' },
  custom: { icon: Gift, label: 'You won a prize!', cta: 'Claim Prize', color: 'indigo' },
  shoutout: { icon: Megaphone, label: 'You got a shoutout!', cta: 'View', color: 'purple' },
};

function useCountdown(expiresAt?: string): string {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!expiresAt) return;

    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('Expired');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return remaining;
}

export default function PrizeClaimBanner({ prize, onClaim, onDismiss }: PrizeClaimBannerProps) {
  const config = TYPE_CONFIG[prize.type] || TYPE_CONFIG.custom;
  const Icon = config.icon;
  const countdown = useCountdown(prize.expiresAt);
  const isRepick = prize.type === 'pick_next_song' && prize.repickReason !== undefined;
  const label = isRepick ? "Your last pick wasn't approved — choose another!" : config.label;

  return (
    <div className="animate-slide-down relative mx-2 mb-3 rounded-xl bg-gradient-to-r from-indigo-500 to-emerald-500 p-[2px] shadow-lg z-40">
      <div className="rounded-[10px] bg-white dark:bg-gray-900 p-4">
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>

        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
            <Icon className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-white text-sm">
              {label}
            </p>
            {prize.type === 'custom' && prize.customText && (
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{prize.customText}</p>
            )}
            {countdown && countdown !== 'Expired' && (
              <div className="flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">{countdown} remaining</span>
              </div>
            )}
            {countdown === 'Expired' && (
              <p className="text-xs text-red-500 font-medium">Prize expired</p>
            )}
          </div>
          {countdown !== 'Expired' && (
            <button
              onClick={onClaim}
              className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white transition-colors"
            >
              {config.cta}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
